import { Component, inject, output, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { numeric, PersistQueryService } from '@peca/commons';
import { PurchaseOrderQuery } from '../../../models/purchase-order/purchase-order-query.model';
import { AutocompleteItemSource } from '@peca/design-system';
import { BehaviorSubject } from 'rxjs';
import { PurchaseOrderService } from '../../../services/purchase.service';

@Component({
  selector: 'peca-fiscal-panel-search',
  templateUrl: './fiscal-panel-search.component.html',
  styleUrls: ['./fiscal-panel-search.component.scss'],
  standalone: false,
})
export class FiscalPanelSearchComponent implements OnInit {
  private readonly searchFormInitialValue = {
    commerceReference: '',
    date: {
      startDate: '',
      endDate: '',
    },
    branch: null,
    amount: 0,
    inboundInvoiceCode: '',
  };
  searchForm: FormGroup;
  branches$: BehaviorSubject<AutocompleteItemSource>;

  persistQueryService: PersistQueryService;
  servicePurchaseOrder: PurchaseOrderService;
  route: ActivatedRoute;

  search = output<PurchaseOrderQuery>();

  get formValues() {
    return {
      commerceReference: this.searchForm.get('commerceReference')?.value,
      date: this.searchForm.get('date')?.value,
      branch: this.searchForm.get('branch')?.value,
      amount: numeric(this.searchForm.get('amount')?.value || '') || 0,
      inboundInvoiceCode: this.searchForm.get('inboundInvoiceCode')?.value,
    };
  }

  get query(): PurchaseOrderQuery {
    return {
      commerceReference: this.formValues.commerceReference,
      startDate: this.formValues.date?.startDate,
      endDate: this.formValues.date?.endDate,
      branchId: this.formValues.branch?.id || null,
      amount: this.formValues.amount || undefined,
      inboundInvoiceCode: this.formValues.inboundInvoiceCode,
    };
  }

  constructor() {
    this.servicePurchaseOrder = inject(PurchaseOrderService);
    this.route = inject(ActivatedRoute);
    this.persistQueryService = inject(PersistQueryService);

    this.searchForm = new FormGroup({
      commerceReference: new FormControl(this.searchFormInitialValue.commerceReference),
      date: new FormControl(this.searchFormInitialValue.date),
      branch: new FormControl(this.searchFormInitialValue.branch),
      amount: new FormControl(this.searchFormInitialValue.amount),
      inboundInvoiceCode: new FormControl(this.searchFormInitialValue.inboundInvoiceCode),
    });
    this.branches$ = new BehaviorSubject<AutocompleteItemSource>({});
  }

  ngOnInit() {
    this.searchForm.valueChanges.subscribe(() => this.checkIfSearchIsPrestine());
    this.route.queryParams.subscribe((params) => {
      if (!params['q']) return;
      const { commerceReference, startDate, endDate, branch, amount, inboundInvoiceCode } = JSON.parse(atob(params['q']));
      this.searchForm.patchValue({
        commerceReference,
        date: { startDate, endDate },
        branch,
        amount,
        inboundInvoiceCode,
      });
    });
  }

  checkIfSearchIsPrestine() {
    const currentValue = this.formValues;
    const isFormEmpty = JSON.stringify(currentValue) === JSON.stringify(this.searchFormInitialValue);
    if (isFormEmpty) return this.searchForm.markAsPristine();
    this.searchForm.markAsDirty();
  }

  onSubmit() {
    this.persistQueryService.rewriteQueryString('peca-purchase-fiscal-panel-list', { ...this.query, branch: this.formValues.branch });
    this.search.emit(this.query);
  }

  clearFilters() {
    this.searchForm.reset(this.searchFormInitialValue);
    this.onSubmit();
  }

  onAutocompleteBranches(search: string) {
    if (search.length < 3) return;

    this.branches$.next({ loading: true });
    this.servicePurchaseOrder.suggestSellersBranches(search).subscribe({
      next: (data) => this.branches$.next({ data }),
      error: () => this.branches$.next({ failure: true }),
    });
  }
}
